import {lazy, useMemo} from 'react';
import {useSelector} from 'react-redux';
import './Config.less';
import Auth from "../Auth/Auth";
import {getCurrentUserId} from "@/lib/picos/currentLogin";
import Icons, {IconType} from "../ui/IconButton/Icons";
import CategoryNav, {CategoryNavCategory} from '../ui/CategoryNav/CategoryNav';
import type {RootState} from '@/@types/store';

// Config Routes
const FirmInfo = lazy(() => import('./FirmInfo/FirmInfo'));
const SetupWizard = lazy(() => import('./SetupWizard/SetupWizard'));
const MasterCategories = lazy(() => import('./MasterCategories/MasterCategories'));
const UserManagement = lazy(() => import('./UserManagement/UserManagement'));
const Dockets = lazy(() => import("./Dockets/Dockets"));
const Banking = lazy(() => import("./Banking/Banking"));
const PaymentSettings = lazy(() => import("./Banking/PaymentSettings"));
const Activities = lazy(() => import("./Activities/Activities"));
const InvoiceConfig = lazy(() => import("./Invoicing/InvoiceConfig"));
const Groups = lazy(() => import("./Groups/Groups"));
const ImportExport = lazy(() => import('./ImportExport/ImportExport'));
const LeadSettings = lazy(() => import('./LeadSettings/LeadSettings'));
const ReportingConfig = lazy(() => import('./Reporting/ReportingConfig'));
const TemplateTasksConfig = lazy(() => import('./TemplateTasks/TemplateTasks'));
const TaskSettingsConfig = lazy(() => import('./TaskOptions/TaskOptions'));
const Courts = lazy(() => import('./Courts/Courts'));
const DeadlineRulesConfig = lazy(() => import('./DeadlineRules/DeadlineRules'));
const TimeOff = lazy(() => import('./TimeOff/TimeOff'));
const EventCategories = lazy(() => import('./EventCategories/EventCategories'));
const ExpenseCategories = lazy(() => import('./ExpenseCategories/ExpenseCategories'));
const IdentificationConfig = lazy(() => import('./Identification/Identification'));
const WinLossReasons = lazy(() => import('./WinLossReasons/WinLossReasons'));
const RelationshipTypes = lazy(() => import('./RelationshipTypes/RelationshipTypes'));
const ParticipantRoles = lazy(() => import('./ParticipantRoles/ParticipantRoles'));
const CaseTypesLeads = lazy(() => import('./CaseTypes/CaseTypesLeads'));
const CaseTypesCases = lazy(() => import('./CaseTypes/CaseTypesCases'));
const RedFlagConfigLeads = lazy(() => import('./RedFlags/RedFlagConfigLeads'));
const RedFlagConfigCases = lazy(() => import('./RedFlags/RedFlagConfigCases'));
const Accessibility = lazy(() => import('./Accessibility/Accessibility'));
const QuestionsConfigContacts = lazy(() => import('./Questions/QuestionsConfigContacts'));
const QuestionsConfigLeads = lazy(() => import('./Questions/QuestionsConfigLeads'));
const QuestionsConfigCases = lazy(() => import('./Questions/QuestionsConfigCases'));

// Types
export type ConfigItemExtended = {
	id: string;
	label: string;
	page: string;
	icon: IconType;
	color?: string;
	requiresAdmin?: boolean;
	component: React.ComponentType;
};

export type ConfigCategoryExtended = {
	id: string;
	name: string;
	description?: string;
	icon: IconType;
	items: ConfigItemExtended[];
};

// Category configuration
export const CONFIG_CATEGORIES: ConfigCategoryExtended[] = [
	{
		id: 'general',
		name: 'General',
		description: 'Your personal preferences',
		icon: Icons.CONFIG,
		items: [
			// Deliberately not admin-gated: these are per-user preferences, so
			// every user needs to reach them for their own account.
			{id: 'accessibility', label: 'Accessibility', page: 'general/accessibility', icon: Icons.FONT, component: Accessibility},
		]
	},
	{
		id: 'firm',
		name: 'Firm',
		description: 'Profile, people & access',
		icon: Icons.BUSINESS,
		items: [
			{id: 'firmInfo', label: 'Firm Info', page: 'firm/info', icon: Icons.BUSINESS, color: 'royalblue', requiresAdmin: true, component: FirmInfo},
			{id: 'firmSetup', label: 'Firm setup', page: 'firm/setup', icon: Icons.CONFIG, requiresAdmin: true, component: SetupWizard},
			{id: 'userManagement', label: 'Users', page: 'firm/users', icon: Icons.USERS, requiresAdmin: true, component: UserManagement},
			{id: 'groups', label: 'Groups', page: 'firm/groups', icon: Icons.GROUP, color: 'navy', requiresAdmin: true, component: Groups},
			{id: 'importExport', label: 'Data Export', page: 'firm/export', icon: Icons.IMPORT, requiresAdmin: true, component: ImportExport},
		]
	},
	{
		id: 'financial',
		name: 'Financial',
		description: 'Money & billing setup',
		icon: Icons.DOLLAR,
		items: [
			{id: 'banking', label: 'Banking', page: 'financial/banking', icon: Icons.BANK, requiresAdmin: true, component: Banking},
			{id: 'paymentOptions', label: 'Payment Options', page: 'financial/payment-options', icon: Icons.CREDIT_CARD_PLAIN, requiresAdmin: true, component: PaymentSettings},
			{id: 'invoicing', label: 'Invoicing', page: 'financial/invoicing', icon: Icons.INVOICE, color: '#FF66CC', requiresAdmin: true, component: InvoiceConfig},
			{id: 'reporting', label: 'Reports', page: 'financial/reports', icon: Icons.CHART, color: '#5C6BC0', requiresAdmin: true, component: ReportingConfig},
		]
	},
	{
		id: 'contacts',
		name: 'Contacts',
		description: 'People & relationships',
		icon: Icons.RELATIONSHIPS,
		items: [
			{id: 'identification', label: 'Identification Methods', page: 'contacts/identification', icon: Icons.ID, color: 'blue', requiresAdmin: true, component: IdentificationConfig},
			{id: 'relationshipTypes', label: 'Relationship Types', page: 'contacts/relationship-types', icon: Icons.RELATIONSHIPS, color: '#3a6172', requiresAdmin: true, component: RelationshipTypes},
			{id: 'participantRoles', label: 'Participant Roles', page: 'contacts/participant-roles', icon: Icons.RELATIONSHIPS, color: '#3a6172', requiresAdmin: true, component: ParticipantRoles},
			{id: 'questionsContacts', label: 'Intake & Custom Fields', page: 'contacts/intake', icon: Icons.QUESTION_FILLED, requiresAdmin: true, component: QuestionsConfigContacts},
		]
	},
	{
		id: 'leads',
		name: 'Leads',
		description: 'Intake & qualification',
		icon: Icons.LEAD,
		items: [
			{id: 'leadSettings', label: 'Lead Settings', page: 'leads/settings', icon: Icons.LEAD, requiresAdmin: true, component: LeadSettings},
			{id: 'caseTypesLeads', label: 'Case Types', page: 'leads/case-types', icon: Icons.CASE, requiresAdmin: true, component: CaseTypesLeads},
			{id: 'winLossReasons', label: 'Win/Loss Reasons', page: 'leads/win-loss-reasons', icon: Icons.FACE_WON, color: 'darkgreen', requiresAdmin: true, component: WinLossReasons},
			{id: 'redFlagsLeads', label: 'Red Flags', page: 'leads/red-flags', icon: Icons.FLAG, requiresAdmin: true, component: RedFlagConfigLeads},
			{id: 'questionsLeads', label: 'Intake & Custom Fields', page: 'leads/intake', icon: Icons.QUESTION_FILLED, requiresAdmin: true, component: QuestionsConfigLeads},
		]
	},
	{
		id: 'cases',
		name: 'Cases',
		description: 'Cases & deadlines',
		icon: Icons.CASE,
		items: [
			{id: 'caseTypesCases', label: 'Case Types', page: 'cases/case-types', icon: Icons.CASE, requiresAdmin: true, component: CaseTypesCases},
			{id: 'expenseCategories', label: 'Expense Types', page: 'cases/expense-types', icon: Icons.EXPENSE, color: 'green', requiresAdmin: true, component: ExpenseCategories},
			{id: 'eventCategories', label: 'Event Types', page: 'cases/event-types', icon: Icons.CALENDAR_DAY, color: 'pink', requiresAdmin: true, component: EventCategories},
			{id: 'courts', label: 'Courts', page: 'cases/courts', icon: Icons.GAVEL, requiresAdmin: true, component: Courts},
			{id: 'dockets', label: 'Dockets', page: 'cases/dockets', icon: Icons.GAVEL, requiresAdmin: true, component: Dockets},
			{id: 'deadlineRules', label: 'Deadline Rules', page: 'cases/deadline-rules', icon: Icons.CLOCK, requiresAdmin: true, component: DeadlineRulesConfig},
			{id: 'redFlagsCases', label: 'Red Flags', page: 'cases/red-flags', icon: Icons.FLAG, requiresAdmin: true, component: RedFlagConfigCases},
			{id: 'questionsCases', label: 'Intake & Custom Fields', page: 'cases/intake', icon: Icons.QUESTION_FILLED, requiresAdmin: true, component: QuestionsConfigCases},
		]
	},
	{
		id: 'tasks',
		name: 'Tasks',
		description: 'Work & templates',
		icon: Icons.TASKS,
		items: [
			{id: 'categories', label: 'Categories', page: 'tasks/categories', icon: Icons.TAGS, color: 'green', requiresAdmin: true, component: MasterCategories},
			{id: 'activities', label: 'Activities', page: 'tasks/activities', icon: Icons.ACTIVITY, color: 'red', requiresAdmin: true, component: Activities},
			{id: 'templateTasks', label: 'Templates', page: 'tasks/templates', icon: Icons.CLONE, color: 'teal', requiresAdmin: true, component: TemplateTasksConfig},
			{id: 'taskSettings', label: 'Task Settings', page: 'tasks/settings', icon: Icons.CHECKED, requiresAdmin: true, component: TaskSettingsConfig},
		]
	},
	{
		id: 'hr',
		name: 'HR',
		description: 'Team time off',
		icon: Icons.CALENDAR,
		items: [
			{id: 'timeOff', label: 'Holidays & Time Off', page: 'hr/time-off', icon: Icons.CALENDAR, requiresAdmin: true, component: TimeOff},
		]
	},
];

function Config(_props: any) {
	// Subscribe to users so we re-render when auth data loads on page reload.
	// Not read inside the memo — the subscription alone is enough to drive a
	// re-render, after which `isAdmin` is re-evaluated and, if it changed, the
	// memo recomputes via its `[isAdmin]` dep.
	useSelector((state: RootState) => state.users);
	const isAdmin = Auth.isAdmin(getCurrentUserId());

	// Filter categories to remove admin-only items if not admin
	const filteredCategories: CategoryNavCategory[] = useMemo(() => {
		return CONFIG_CATEGORIES.map(category => ({
			...category,
			items: category.items
				.filter(item => !item.requiresAdmin || isAdmin)
				.map(({requiresAdmin: _, ...item}) => item)
		})).filter(category => category.items.length > 0);
	}, [isAdmin]);

	return (
		<CategoryNav
			basePath="/config"
			cacheKey="config-page"
			categories={filteredCategories}
			mobileBreakpoint={700}
			className="config"
		/>
	);
}

export default Config;
